import { useRouter } from 'next/router';
import usePromise from './usePromise';
import { useRef, useState, useMemo } from 'react';
import axios from 'axios';

const EMPTY_OBJECT = {};

/**
 * Return object with all items to display, function to request more items and additional helpers
 * @param {Function} resource - async function to perform request
 * @param {Array} initialState for items
 * @param {Object} query - additional dependencies except of page
 * @return {Object} result
 * @return {Array} result.items - list of items fro current page
 * @return {boolean} result.hasMore - return true if more items could be requested
 * @return {Function} result.handleLoadMore - function to request more items
 * @return {boolean} result.loading - return true if request in pending status
 */

export default function useLoadMore(resource, initialState, query = EMPTY_OBJECT, inner = false) {
  const router = useRouter();
  const {
    query: { page: queryPage, ...queryParams },
  } = router;
  const page = Number(queryPage) || 1;
  const [current, setCurrent] = useState({ page:1});

  const cancelTokenSource = useRef(axios.CancelToken.source());

  const q = useMemo(() => (!inner ? query : current), [inner, query, current]);
  const p = useMemo(() => (!inner ? page : current.page), [inner, page, current]);
 
  const promise = usePromise(
    () => resource(p, { ...q, cancelToken: cancelTokenSource.current.token }),
    [p, q],
    initialState,
    () => cancelTokenSource.current.cancel()
  );

  const handleLoadMore = (p) => {
    const query = { ...queryParams };
    query.page = page + 1;

    if (inner) {
      setCurrent({ page: p + 1 });
    } else {
      router.replace({ pathname: router.pathname, query }, { pathname: location.pathname, query }, { shallow: true });
    }
  };

  return {
    items: promise.result.items,
    total: promise.result.total,
    hasMore: promise.result.total > promise.result.items.length,
    page: inner ? current.page : query.page,
    handleLoadMore,
    loading: promise.status === 'pending',
  };
}
